/**
 * The Summary Reports page (/workflow/reporting).
 *
 * Owns the layout, the grid's apiRef, and which report is active.
 */

import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
    AppBar,
    Box,
    Chip,
    Divider,
    IconButton,
    Paper,
    Popover,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGridApiRef } from '@mui/x-data-grid-premium';

import { setShowMap } from '../../../../redux/slices/displayStateSlice';
import { hydrateFilters, setDefinitionExpression } from '../../../../redux/slices/filterSlice';
import { captureFilterState, resolveFilterCriteria } from '../../utils/filterSnapshot';
import ColorConstants from '../../../../defaults/ColorConstants.jsx';
import type { RootState } from '../../../../redux/store.tsx';
import type {
    SummaryReport,
    SummaryReportChartState,
    SummaryReportDialogMode,
    SummaryReportGridState,
    SummaryReportPivotState,
    SummaryReportUserInfo,
} from '../../types.ts';
import { SummaryReportService } from '../../../../services/SummaryReportService.ts';
import { capturePivotState } from '../../utils/pivotState.ts';
import { captureChartState, type ChartContextLookup } from '../../utils/chartPersistence.ts';

import SummaryReportGrid from '../SummaryReportsGrid/SummaryReportsGrid.tsx';
import SummaryReportsBoundary from '../SummaryReportsBoundary/SummaryReportsBoundary.tsx';
import SummaryReportsToolbar from '../SummaryReportsToolbar/SummaryReportsToolbar.tsx';
import {
    LAYOUT_SX,
    readStoredLayout,
    storeLayout,
    type SummaryReportLayout,
} from '../../utils/layout.ts';
import SummaryReportsDialog from '../SummaryReportsDialog/SummaryReportsDialog.tsx';
import {
    SummaryReportsStatusLine,
    SummaryReportsFilterLine,
    SummaryReportsNoticeToast,
} from '../SummaryReportsNotice/SummaryReportsNotice.tsx';
import { useSummaryReportNotice } from '../../hooks/useSummaryReportNotice.ts';
import { useSummaryReportStatus } from '../../hooks/useSummaryReportStatus.ts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const { WhiteAlien, BlueAlien, LightGrayAlien } = ColorConstants;

function SummaryReportsViewContent() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const definitionExpression = useSelector(
        (state: RootState) => state.filter.definitionExpression
    );

    // How many filters are behind that expression, for the header. Which count is live
    // depends on which filter UI is driving — the same pair Header.jsx reads.
    const basicFilterCount = useSelector((state: RootState) => state.filter.activeFilterCount);
    const customFilterCount = useSelector((state: RootState) => state.filter.customFilterCount);
    const isCustomFilterActive = useSelector(
        (state: RootState) => state.filter.isCustomFilterActive
    );
    const activeFilterCount = isCustomFilterActive ? customFilterCount : basicFilterCount;
    // globalContext is untyped JS and starts as null. See SummaryReportUserInfo.
    const userInfo = useSelector(
        (state: RootState) => state.globalContext.userInfo
    ) as SummaryReportUserInfo | null;
    // The Users-layer row, not a portal user: NifcUsername and Email are its own fields.
    const userId: string = userInfo?.NifcUsername ?? userInfo?.Email ?? '';

    const headerHeight = useSelector((state: RootState) => state.displayState.headerHeight);

    // Owned here rather than by the grid, because saving a report needs exportState.
    const gridApiRef = useGridApiRef();

    // The chart's type and configuration, kept current by the grid. A save needs it, but it
    // can only come from the grid — that state lives inside the integration's provider.
    const chartContextRef = useRef<ChartContextLookup>({});

    // ── The active report ──
    const [activeSummaryReport, setActiveSummaryReport] = useState<SummaryReport | null>(null);
    const [initialGridState, setInitialGridState] = useState<SummaryReportGridState | undefined>(
        undefined
    );
    // Beside initialGridState rather than inside it, since exportState() drops pivoting.
    const [initialPivotState, setInitialPivotState] = useState<SummaryReportPivotState | undefined>(
        undefined
    );
    // Beside it too, for a different reason: pivoting's export overwrites the chart.
    const [initialChartState, setInitialChartState] = useState<SummaryReportChartState | undefined>(
        undefined
    );

    // Names the user typed, per chart id. Plain state rather than a ref, because this one is
    // ours to read and a load has to push a new set down. Empty means every chart falls back
    // to its description.
    const [chartTitles, setChartTitles] = useState<Record<string, string>>({});
    const handleChartTitleChange = useCallback((chartId: string, title: string) => {
        setChartTitles((current) => {
            // Blank asks for the generated name back, so drop the key rather than keep an
            // empty string that would read as a name.
            const next = { ...current };
            if (title.trim()) next[chartId] = title.trim();
            else delete next[chartId];
            return next;
        });
    }, []);

    // ── Dialog state: open, mode, and the report awaiting confirmation ──
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<SummaryReportDialogMode>('save');
    // Held here because it arrives from two places: a row in the list, or a ?reportId= link
    // only the View can resolve.
    const [pendingReport, setPendingReport] = useState<SummaryReport | null>(null);

    // Reported up from the grid because the arrangement toggle sits in the header, outside
    // the charts integration's provider.
    const [chartVisible, setChartVisible] = useState(false);
    const handleChartVisibleChange = useCallback((visible: boolean) => {
        setChartVisible(visible);
    }, []);

    // Confirmations for actions that happen inside the dialog and would otherwise finish
    // invisibly. Owned here because the toast outlives the dialog that raised it — saving
    // closes it — and the info panel keeps the notice afterwards.
    const { notice, toastOpen, notify, dismissToast } = useSummaryReportNotice();

    // The "about" popover: what this view is, and how reports are saved.
    const [aboutAnchorEl, setAboutAnchorEl] = useState<HTMLElement | null>(null);
    const openAbout = useCallback((event: MouseEvent<HTMLElement>) => {
        setAboutAnchorEl(event.currentTarget);
    }, []);
    const closeAbout = useCallback(() => {
        setAboutAnchorEl(null);
    }, []);

    // The map stays hidden for the life of this page.
    useEffect(() => {
        dispatch(setShowMap(false));
        return () => {
            dispatch(setShowMap(true));
        };
    }, [dispatch]);

    const handleClose = useCallback(() => {
        dispatch(setShowMap(true));
        navigate('/home');
    }, [dispatch, navigate]);

    // Remembered per user, not part of a saved report.
    const [layout, setLayout] = useState<SummaryReportLayout>(() => readStoredLayout());
    const handleLayoutChange = useCallback((next: SummaryReportLayout) => {
        setLayout(next);
        storeLayout(next);
        console.log(next);
    }, []);
    const layoutSx = LAYOUT_SX[layout];

    // ─── Dialog outcomes ─────────────────────────────────────────────────────
    // The dialog owns its own logic. These are the outcomes the View has a stake in: what's
    // on screen, and which report is active.

    // The toolbar picks the mode to open in; from there the dialog drives its own.
    const handleOpenDialog = useCallback((mode: SummaryReportDialogMode) => {
        setDialogMode(mode);
        setDialogOpen(true);
    }, []);

    // Closing abandons whatever was awaiting confirmation. Mode changes don't come through
    // here, so the "Save Current First" detour keeps its pending report.
    const handleDialogClose = useCallback(() => {
        setDialogOpen(false);
        setPendingReport(null);
    }, []);

    // The one thing the dialog reads back out of the View: the grid, ready to save. Three
    // pieces — the exported blob, the pivot it leaves out, and the chart it carries a stale
    // copy of while pivoted.
    const exportGridState = useCallback(() => {
        const gridState = gridApiRef.current?.exportState?.() ?? {};
        const pivotState = capturePivotState(gridApiRef.current?.state?.pivoting);
        const chartState = captureChartState(
            gridApiRef.current?.state?.chartsIntegration,
            chartContextRef.current,
            chartTitles
        );
        return { gridState, pivotState, chartState };
    }, [gridApiRef, chartTitles]);

    // The filter half of that snapshot: the criteria the expression was compiled from, which
    // the form reads and the clause can't be turned back into.
    //
    // Read off the store at save time rather than selected — it is only wanted at the moment
    // of saving, and subscribing would re-render on every keystroke in the filter form.
    const store = useStore<RootState>();
    const snapshotFilterState = useCallback(() => captureFilterState(store.getState()), [store]);

    // The same snapshot to a second end: whether it still matches the report it came from.
    // The header is where someone looks before walking away from unsaved work.
    const reportStatus = useSummaryReportStatus({
        apiRef: gridApiRef,
        activeReport: activeSummaryReport,
        definitionExpression: definitionExpression ?? '',
        exportGridState,
    });

    // Loading takes two steps, because it overwrites the current filter and grid.
    // First: a row was picked, so hold it while the dialog confirms.
    const handleSelectReportForLoad = useCallback((report: SummaryReport) => {
        setPendingReport(report);
    }, []);

    // Then: confirmed, so apply the report and make it active.
    const handleReportLoadConfirmed = useCallback(
        (report: SummaryReport) => {
            // Criteria first, then the expression: the criteria put the form back the way it
            // was saved, or the data would filter correctly while the form showed whatever
            // the user last set by hand. A report with no criteria leaves the form alone and
            // still filters from the expression.
            if (report.filterState) {
                dispatch(hydrateFilters(resolveFilterCriteria(report.filterState)));
            }
            dispatch(setDefinitionExpression(report.definitionExpression));
            setInitialGridState(report.gridState);
            setInitialPivotState(report.pivotState);
            setInitialChartState(report.chartState);
            setChartTitles(report.chartState?.titles ?? {});
            setActiveSummaryReport(report);
            setPendingReport(null);
        },
        [dispatch]
    );

    // Saved or updated: that report is now the active one.
    const handleActiveReportChange = useCallback((report: SummaryReport) => {
        setActiveSummaryReport(report);
    }, []);

    // Renamed or shared. Row actions reach reports that aren't loaded, so reconcile by id
    // rather than promoting whatever was touched.
    const handleReportChanged = useCallback((report: SummaryReport) => {
        setActiveSummaryReport((current) => (current?.id === report.id ? report : current));
    }, []);

    // Deleted. Same reconciliation. The grid and filter stay put: the data is still there,
    // it just isn't a named report any more.
    const handleReportDeleted = useCallback((id: string) => {
        setActiveSummaryReport((current) => (current?.id === id ? null : current));
    }, []);

    // The other way in: a ?reportId= link. The same pending-then-confirm handshake as
    // picking a row, opened straight to the confirm step.
    //
    useEffect(() => {
        const reportId = searchParams.get('reportId');
        if (!reportId) return undefined;

        // A link resolved after the user navigated away must not open the dialog on them.
        let abandoned = false;
        SummaryReportService.getSummaryReportById(reportId)
            .then((report) => {
                if (abandoned || !report) return;
                setPendingReport(report);
                setDialogMode('confirmOverwrite');
                setDialogOpen(true);
            })
            .catch(() => {
                // A bad or unreachable link leaves the view on live filter results.
            });

        return () => {
            abandoned = true;
        };
    }, [searchParams]);

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                backgroundColor: LightGrayAlien,
            }}
        >
            {/* ── AppBar ── */}
            <AppBar
                sx={{ position: 'relative', flexShrink: 0 }}
                style={{ backgroundColor: BlueAlien, marginTop: headerHeight, userSelect: 'none' }}
            >
                <Toolbar sx={{ gap: 2, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <IconButton
                            edge='start'
                            color='inherit'
                            onClick={handleClose}
                            aria-label='close'
                        >
                            <CloseIcon style={{ fill: WhiteAlien }} />
                        </IconButton>
                        <Typography
                            sx={{ ml: 2 }}
                            variant='h6'
                            component='div'
                            style={{ color: WhiteAlien }}
                        >
                            Summary Reports
                        </Typography>
                    </div>

                    {/* ── Left: Info / Messages ── */}
                    <Paper
                        elevation={1}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            // Sized to its content, with the 40% a ceiling rather than a
                            // target, so it never holds width it isn't using.
                            flex: '0 1 auto',
                            minWidth: 0,
                            borderRadius: 2,
                            padding: '8px 16px !important',
                            maxWidth: '40%',
                        }}
                    >
                        <Tooltip
                            slotProps={{ tooltip: { sx: { fontSize: 12 } } }}
                            title='About Summary Reports'
                        >
                            <IconButton
                                size='small'
                                onClick={openAbout}
                                aria-label='about summary reports'
                                sx={{ p: 0 }}
                            >
                                <InfoOutlinedIcon fontSize='small' color='action' />
                            </IconButton>
                        </Tooltip>
                        <Divider
                            orientation='vertical'
                            flexItem
                            sx={{ my: 0.5, borderColor: 'divider' }}
                        />
                        {activeSummaryReport?.name ? (
                            <Chip
                                label={activeSummaryReport?.name}
                                size='small'
                                color='primary'
                                variant='outlined'
                            />
                        ) : (
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                noWrap
                                sx={{ minWidth: 0 }}
                                title='No saved report loaded — these are live filter results'
                            >
                                Live filter results
                            </Typography>
                        )}
                        {/* Only with a report to be up to date with. Skipped whole — divider
                            included — rather than left as an empty crumb. */}
                        {reportStatus !== 'none' && (
                            <>
                                <Divider
                                    orientation='vertical'
                                    flexItem
                                    sx={{ my: 0.5, borderColor: 'divider' }}
                                />
                                <SummaryReportsStatusLine status={reportStatus} />
                            </>
                        )}
                        <Divider
                            orientation='vertical'
                            flexItem
                            sx={{ my: 0.5, borderColor: 'divider' }}
                        />
                        <SummaryReportsFilterLine count={activeFilterCount ?? 0} />
                    </Paper>

                    {/* ── About popover ── */}
                    <Popover
                        open={Boolean(aboutAnchorEl)}
                        anchorEl={aboutAnchorEl}
                        onClose={closeAbout}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { maxWidth: 420, p: 2, borderRadius: 2 } } }}
                    >
                        <Typography variant='subtitle2' gutterBottom>
                            About Summary Reports
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            This view displays summary data based on your active filters. You can
                            customize the grid — adjust columns, grouping, sorting, and aggregation
                            — then <strong>Save</strong> that configuration as a named report. Saved
                            reports can be <strong>Loaded</strong> later or
                            <strong> Shared</strong> with teammates via link or direct access.
                        </Typography>
                    </Popover>

                    {/* ── Controls ── */}
                    {/* No wrapper: the controls carry their own paper and lift, so they sit
                        directly on the header. */}
                    <SummaryReportsToolbar
                        activeReportName={activeSummaryReport?.name}
                        reportStatus={reportStatus}
                        chartVisible={chartVisible}
                        onOpenDialog={handleOpenDialog}
                        layout={layout}
                        onLayoutChange={handleLayoutChange}
                    />
                </Toolbar>
            </AppBar>

            {/* ── Content area with islands ── */}
            <Box
                sx={{
                    flex: 1,
                    ...layoutSx.viewContent,
                    padding: '16px !important',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {/* ── Grid Island ── */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        ...layoutSx.gridPaper,
                    }}
                >
                    {/* Its own boundary, so a grid or chart crash leaves the header usable. */}
                    <SummaryReportsBoundary label='The report grid'>
                        <SummaryReportGrid
                            apiRef={gridApiRef}
                            initialGridState={initialGridState}
                            initialPivotState={initialPivotState}
                            initialChartState={initialChartState}
                            chartTitles={chartTitles}
                            onChartTitleChange={handleChartTitleChange}
                            chartContextRef={chartContextRef}
                            onChartVisibleChange={handleChartVisibleChange}
                            layout={layout}
                        />
                    </SummaryReportsBoundary>
                </Box>
            </Box>

            {/* ── Dialog (owns all its own logic) ── */}
            <SummaryReportsDialog
                open={dialogOpen}
                mode={dialogMode}
                onClose={handleDialogClose}
                onModeChange={setDialogMode}
                userId={userId}
                definitionExpression={definitionExpression ?? ''}
                activeReport={activeSummaryReport}
                pendingReport={pendingReport}
                exportGridState={exportGridState}
                captureFilterState={snapshotFilterState}
                onSelectReportForLoad={handleSelectReportForLoad}
                onReportLoadConfirmed={handleReportLoadConfirmed}
                onActiveReportChange={handleActiveReportChange}
                onReportChanged={handleReportChanged}
                onReportDeleted={handleReportDeleted}
                onNotify={notify}
            />

            <SummaryReportsNoticeToast notice={notice} open={toastOpen} onClose={dismissToast} />
        </Box>
    );
}

/** Wrapped so a crash anywhere on the page shows a message rather than a blank screen. */
export default function SummaryReportsView() {
    return (
        <SummaryReportsBoundary label='Summary Reports'>
            <SummaryReportsViewContent />
        </SummaryReportsBoundary>
    );
}
